/* eslint-disable @typescript-eslint/dot-notation */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { A4_PAGE_HEIGHT, B5_PAGE_HEIGHT, LEGAL_PAGE_HEIGHT, LETTER_PAGE_HEIGHT } from '@core/const/page-height';
import { A4_PAGE_WIDTH, B5_PAGE_WIDTH, LEGAL_PAGE_WIDTH, LETTER_PAGE_WIDTH } from '@core/const/page-width';
import { AuthorizedSignatory } from '@core/enums/authorized-signatory';
import { InspectionTypes } from '@core/enums/inspection-types';
import { ScreenOrientations } from '@core/enums/screen-orientations';
import { SurveyMeasurementStatuses } from '@core/enums/survey-measurement-statuses';
import { MeasurementHelper } from '@core/helpers/measurement.helper';
import { AlarmConfig } from '@core/interfaces/alarm-config';
import { FileInfo } from '@core/interfaces/file-info';
import { ImageInfo } from '@core/interfaces/image-info';
import { Image, Measurement } from '@core/interfaces/measurement';
import { TemplateOfflineImage } from '@core/interfaces/offline/template-offline-image';
import { DropDownElementStyleObject } from '@core/interfaces/report-template/dropdown-element-style';
import { ElementsStyleObject } from '@core/interfaces/report-template/elements-style';
import { ListItem } from '@core/interfaces/report-template/list-item';
import { TemplateMeasurementPoint } from '@core/interfaces/report-template/measurement-points';
import { ReportElement } from '@core/interfaces/report-template/report-element';
import { ReportPage } from '@core/interfaces/report-template/report-page';
import { ReportSignature } from '@core/interfaces/report-template/report-signature';
import { TemplateSignature } from '@core/interfaces/report-template/template-signature';
import { TemplateVisualEvents } from '@core/interfaces/report-template/visual-events';
import { SurveyMeasurement } from '@core/interfaces/survey-measurement';
import { SurveyPoint } from '@core/interfaces/survey-point';
import { VisualEvent } from '@core/interfaces/visual-event';
import { IonicService } from '@core/services/ionic.service';
import { IpmAuthService } from '@core/services/ipm-auth.service';
import { Platform } from '@ionic/angular/standalone';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ReportTablePipe } from '@shared/pipes';
import { LoggerService } from '@shared/services/logger.service';
import _ from 'lodash';
import { SnackBarService } from 'osc-reference-app-common';
import { Subscription, firstValueFrom, lastValueFrom } from 'rxjs';
import { DrawingOptions } from '@mobile/interfaces/drawing-options';
import { CanvasService } from '@mobile/services/canvas.service';
import { staticStylesForIpmReport } from 'src/assets/app.constant';
import { platform } from 'src/platforms/platform';
import { NgStyle, NgTemplateOutlet, NgClass, AsyncPipe, JsonPipe } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { OfflineImagePipe } from '@shared/pipes/offline-image.pipe';
import { VisualEventTypePipe } from '@shared/pipes/visual-event-type.pipe';
import { UserProfile } from '@osc-utils/models/user-profile';
import { OscPlatform } from '@osc-utils/enums/osc-platform';
import { WhiteboardHelper } from '@osc-utils/helper/whiteboard.helper';
import { UserSignatureInfo } from '@osc-utils/models/user-signature-info';
import { WhiteboardUpdate } from '@osc-utils/models/whiteboard-update';
import { SpinnerService } from '@osc-utils/services/spinner.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InteractiveIsometricImageDataFetcher } from '@shared/services/interactive-isometric-image-data-fetcher.service';
import { OrientationService } from '@core/services/orientation.service';
import { ReportService } from '../../services/report.service';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ReportService],
  imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    MatOption,
    NgStyle,
    NgTemplateOutlet,
    MatIcon,
    NgClass,
    AsyncPipe,
    JsonPipe,
    OfflineImagePipe,
    VisualEventTypePipe,
    ReportTablePipe,
    TranslateModule,
  ],
})
export class ReportComponent implements OnInit, OnDestroy {
  @Input() public projectId: string;
  @Input() public jobId: string;
  @Input() public taskId: string;
  @Input() public inspectionType: string;
  @Input() public readOnly = false;

  public changeDetectorRef = inject(ChangeDetectorRef);
  public staticStyles = staticStylesForIpmReport;

  public template: any;
  public initialTemplate: any[] = [];
  public filteredPages: any[];
  public templateFile: FileInfo;
  public offlineImages: Array<TemplateOfflineImage>;
  public redlineData: WhiteboardUpdate[][];
  public measurementData: Measurement[];
  public isValidationOn = false;
  public isoImageUris: Array<string>;
  public isoImageMetaInfo: Array<ImageInfo>;
  public redlineImageData: Array<string>;
  public visualEventsMarkers: VisualEvent[][] = [];
  public veOffsets: Image[] = [];
  public cmls: SurveyPoint[][] = [];
  public cmlOffsets: Image[] = [];
  public isoImage: string | ArrayBuffer;
  public updatedIsoImage: string[] = [];
  public veISOLoaded: boolean[] = [];
  public cmlISOLoaded: boolean[] = [];
  public userFullName: string;
  public userProfile: UserProfile;
  public inspectionReportData: any;
  public isSignSuccessful: boolean;
  public measurementPointPageBreakInfo = [];
  public visualEventsPageBreakInfo = [];
  public allVisualEventData: VisualEvent[] = [];
  public addOffsets = true;
  public visualEventData: VisualEvent[] = [];
  public photoEventsData: VisualEvent[] = [];
  #previewMode = false;
  public linkData: string;
  public autoSaveEnabled = true;
  public isIsoLoading: boolean;
  public cmlBubbleHeight: number;
  public SurveyMeasurementStatuses = SurveyMeasurementStatuses;
  public currentDateTime: string;
  public visualEvents: VisualEvent[] = [];
  public splitVEData: VisualEvent[] = [];
  public isReportUpdating = false;
  public isImageuploaded = false;
  public scale = 1;
  public isMobile: boolean;

  protected authService = inject(IpmAuthService);
  protected currentUserService = inject(CurrentUserService);
  protected currentFocusElement: HTMLTextAreaElement | HTMLSelectElement | HTMLInputElement;
  protected alarmConfig: AlarmConfig[];
  protected readonly availableSpaceOffsetInMm = 3;
  protected readonly measurementTableTitleHeightInMm = 9;
  protected readonly measurementTableRowSafetyBufferInMm = 0.25;
  protected readonly measurementTableHeightSafetyBufferInMm = 1;
  protected readonly measurementTableDomPaginationSafetyBufferInMm = 1;
  readonly #millimetersPerCssPixel = 25.4 / 96;

  #logger = inject(LoggerService);
  #canvasService = inject(CanvasService);
  #reportService = inject(ReportService);
  #fetcher = inject(InteractiveIsometricImageDataFetcher);
  #ionicService = inject(IonicService);
  #plt = inject(Platform);
  #spinnerService = inject(SpinnerService);
  #snackBarService = inject(SnackBarService);
  #translate = inject(TranslateService);
  #orientationService = inject(OrientationService);
  #saveTimerID: NodeJS.Timeout;
  #signatureList: UserSignatureInfo[] = [];
  #originalTemplate: any;
  #savedMeasurementRows = new Map<number, SurveyMeasurement[]>();
  #routeChangeSubscription: Subscription;
  #selectMyReportApprovalLists: Subscription;
  #getTaskInfoSubscription: Subscription;
  #screenOrientationChangeSubscription: Subscription;
  #destroyRef = inject(DestroyRef);

  public constructor() {
    this.isMobile = this.#reportService.isMobile();
  }

  public get previewMode(): boolean {
    return this.#previewMode || this.readOnly;
  }

  public set previewMode(value: boolean) {
    this.#previewMode = value;
  }

  public async ngOnInit(): Promise<void> {
    this.#spinnerService.show(this.#spinnerService.mainSpinnerId);
    this.visualEvents = await firstValueFrom(this.#reportService.getVisualEvents(this.taskId));
    this.scrollToCurrentFocusElementOnlyForIos();
    this.getTemplateInfo();
    this.#screenOrientationChangeSubscription = this.#orientationService.onChange.subscribe((orientation: ScreenOrientations) => {
      this.scaleReport();
    });

    this.currentUserService.currentUser$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(user => {
      this.userProfile = user;
      this.userFullName = this.userProfile.givenName + ' ' + this.userProfile.surname;
    });
  }

  public ngOnDestroy(): void {
    this.#routeChangeSubscription?.unsubscribe();
    this.#selectMyReportApprovalLists?.unsubscribe();
    this.#getTaskInfoSubscription?.unsubscribe();
    this.#screenOrientationChangeSubscription?.unsubscribe();
  }

  public checkChanges(): void {
    if (this.autoSaveEnabled === false) {
      return;
    }
    clearTimeout(this.#saveTimerID);
    this.template.bodies.forEach((itm, pageIndex) => {
      itm.texts?.forEach(text => {
        this.validateTextInput(text);
      });
    });
    this.#saveTimerID = setTimeout(() => this.saveTemplateUpdate(), 1000);
  }

  public checkDynamicEditChanges(txtItem: any): void {
    if (this.autoSaveEnabled === false) {
      return;
    }
    clearTimeout(this.#saveTimerID);

    if (txtItem.type === 'dynamic-editable') {
      // Validate the edited field on every change so a required field that the user
      // clears flags itself (red "*required") right away. This must run unconditionally:
      // the initialTemplate lookup below only feeds the "changed" indicator, and when the
      // field has no matching entry there the mandatory marker would otherwise never update.
      this.validateTextInput(txtItem);
      this.initialTemplate.forEach(itm => {
        itm.texts?.forEach(txt => {
          if (txt.id === txtItem.id) {
            txtItem.isChanged = this.isTextChanged(txt, txtItem);
          }
        });
      });
    }
    this.#saveTimerID = setTimeout(() => this.saveTemplateUpdate(), 1000);
  }

  public editDynamicValueClick(id: any, column: any) {
    if (!this.previewMode) {
      id.allowEdit = column.label;
      this.focusElements();
    }
  }

  public isChangeTemplate(): boolean {
    return JSON.stringify(this.template) === JSON.stringify(this.#originalTemplate);
  }

  public isTextChanged(oldText: any, newText: any): boolean {
    let isTextChanged = false;
    if (oldText.initialText !== newText.text) {
      isTextChanged = true;
    }
    return isTextChanged;
  }

  public saveTemplateUpdate() {
    this.#saveTimerID = undefined;
    if (this.isReportUpdating) {
      // A save request is already in-flight. Reschedule so the pending changes
      // are written only after the current request completes, keeping writes sequential.
      this.#saveTimerID = setTimeout(() => this.saveTemplateUpdate(), 1000);
      return;
    }
    if (!this.isChangeTemplate()) {
      this.isReportUpdating = true;
      const currentTemplate = structuredClone(this.template);
      const promises = [this.saveTemplate(), this.addReportHtml()];

      Promise.all(promises).then(
        () => {
          this.#originalTemplate = currentTemplate;
          this.isReportUpdating = false;
        },
        ex => {
          this.#logger.error('saveTemplateUpdate failed:', ex);
          this.isReportUpdating = false;
        },
      );
    }
  }

  public async validateReportData(): Promise<boolean> {
    this.isValidationOn = true;
    const isTextFieldValid = this.isReportTextFieldValid();
    if (this.inspectionType === InspectionTypes.Visual || this.inspectionType === InspectionTypes.Visual_Baseline) {
      const checkSurveyTable = this.checkSurveyTableData();
      return checkSurveyTable && isTextFieldValid;
    } else if (this.inspectionType === InspectionTypes.UT || this.inspectionType === InspectionTypes.UT_Baseline) {
      const isMeasurementDataTaken = await this.checkMeasurementData();
      return isMeasurementDataTaken && isTextFieldValid;
    }
  }

  public async saveTemplate(): Promise<void> {
    const template = structuredClone(this.template);
    template.bodies = template.bodies.filter(page => !(page.pageType && page.pageType === 'custom'));
    const taskUriToId = this.isMobile
      ? new Map(
          (await firstValueFrom(this.#reportService.getTaskFiles(this.projectId, this.jobId, this.taskId))).all.map(f => [
            f.downloadUri,
            f.id,
          ]),
        )
      : new Map<string, string>();
    template.bodies = template.bodies.map(page => {
      if (page.visualEvents && page.visualEvents.length > 0) {
        delete page.visualEvents[0].visualEventIndex;
      }

      if (page.measurementPoints && page.measurementPoints.length > 0) {
        delete page.measurementPoints[0].tableIndex;
      }
      if (page.images?.length) {
        if (page.images[0].dynamicId === 'photoGallery') {
          delete page.images[0].photoGalleryIndex;
        }
        if (this.isMobile) {
          for (const img of page.images) {
            const fileId = taskUriToId.get(img.src);
            if (fileId) {
              img.src = `{{fileUri(${fileId})}}`;
            }
          }
        }
      }
      return page;
    });
    const templateFileName = this.templateFile.fileName.split(`${this.taskId}-`)[1];
    const fileName = this.isMobile
      ? templateFileName === ''
        ? this.templateFile.fileName.split(`${this.taskId}-`)[2]
        : templateFileName
      : this.templateFile.fileName;
    return await firstValueFrom(
      this.#reportService.updateTemplate(
        this.projectId,
        this.jobId,
        this.taskId,
        this.templateFile.id,
        JSON.stringify(template),
        fileName,
        this.templateFile.description,
        this.templateFile.fileType,
      ),
    );
  }

  public addReportHtml(isRealTime = false): Promise<void> {
    const checkboxStyle = this.staticStyles.commonCheckBoxStyleForPdf;
    const radioStyle = this.staticStyles.commonRadioStyleForPDF;
    const tableStyle = this.staticStyles.tableStyles;
    const textStyle = this.staticStyles.textStyles;
    const fontStyles = `font: 400 0.875rem/1.38 "Roboto"`;
    const pageWidth = this.getPageWidth();
    const pageHeight = this.getPageHeight();
    const sizeUnit = this.template.sizeUnit;
    // Explicit CSS page breaks prevent Puppeteer from slicing the HTML via pixel-height
    // arithmetic, which accumulates sub-pixel rounding errors over many pages (~4mm by page 30).
    // PreferCSSPageSize (set on the backend) honours the @page size over the Puppeteer format.
    const pdfPaginationStyle = `
      @page { size: ${pageWidth}${sizeUnit} ${pageHeight}${sizeUnit}; margin: 0; }
      .ipm-report__page-frame { break-after: page; page-break-after: always; margin: 0 auto !important; }
      .ipm-report__page-frame:last-child { break-after: avoid; page-break-after: avoid; }
    `;
    this.changeDetectorRef.detectChanges();
    const report: string = this.getReportHtmlData();
    this.scaleReport();
    const htmlReport = `<html><head><style>${checkboxStyle} ${radioStyle} ${tableStyle} ${textStyle} ${pdfPaginationStyle}
      body{${fontStyles};height:0%}</style></head><body>${report}</body></html>`;
    return firstValueFrom(
      this.#reportService.addHtmlReport(
        this.projectId,
        this.jobId,
        this.taskId,
        htmlReport,
        'taskReport.html',
        'INSPECTIONREPORTHTML',
        isRealTime,
      ),
    );
  }

  public getStyle(text: ReportElement): ElementsStyleObject {
    let obj: ElementsStyleObject = {
      position: 'absolute',
      whiteSpace: 'normal',
      fontSize: text.fontSize ? text.fontSize + this.template.sizeUnit : 'inherit',
      fontFamily: `${text.fontFamily} !important`,
      color: text.fontColor,
      fontStyle: text.fontStyle,
      fontWeight: text.fontWeight,
      lineHeight: text.lineHeight + this.template.sizeUnit,
      textAlign: text.horizontalAlign,
      verticalAlign: text.verticalAlign,
      backgroundColor: text.backgroundColor,
      left: text.x + this.template.sizeUnit,
      top: text.y + this.template.sizeUnit,
      zIndex: text.zIndex ? text.zIndex : '99',
      paddingTop: text.paddingTop + this.template.sizeUnit,
      paddingRight: text.paddingRight + this.template.sizeUnit,
      paddingBottom: text.paddingBottom + this.template.sizeUnit,
      paddingLeft: text.paddingLeft + this.template.sizeUnit,
    };
    if (text.inputType === 'textArea') {
      // Must follow a similar style to `.ipm-report__pre-field`
      obj.whiteSpace = 'pre-wrap';
      obj.wordWrap = 'break-word';
    }
    if (text.columns) {
      obj.width = text.width ? text.width + this.template.sizeUnit : 'auto';
    } else {
      obj = this.getWidthAndHeights(obj, text);
    }
    if (text.verticalAlign === 'middle') {
      obj['align-items'] = 'center';
      obj.display = 'flex';
    } else if (text.verticalAlign === 'bottom') {
      obj['align-items'] = 'end';
      obj.display = 'flex';
    } else if (text.verticalAlign === 'top') {
      obj['align-items'] = 'top';
      obj.display = 'flex';
    }
    // Todo need cleanup
    if (text.horizontalAlign !== undefined) {
      obj['justify-content'] = text.horizontalAlign;
    }
    return obj;
  }

  public selectEdit(text: ReportElement, event: boolean) {
    if (!event) {
      this.disableEditMode(text);
    }
  }

  public getPageSize(): { width: string; height: string } {
    const style = {
      width: '',
      height: '',
    };
    switch (this.template.paperSize) {
      case 'Letter':
        style.width = '215.9' + this.template.sizeUnit;
        style.height = LETTER_PAGE_HEIGHT + this.template.sizeUnit;
        break;
      case 'Legal':
        style.width = '215.9' + this.template.sizeUnit;
        style.height = LEGAL_PAGE_HEIGHT + this.template.sizeUnit;
        break;
      case 'A4':
        style.width = '210' + this.template.sizeUnit;
        style.height = A4_PAGE_HEIGHT + this.template.sizeUnit;
        break;
      case 'B5':
        style.width = '182' + this.template.sizeUnit;
        style.height = B5_PAGE_HEIGHT + this.template.sizeUnit;
        break;
    }
    return style;
  }

  public getPageStyle(): ElementsStyleObject {
    return {
      width: this.getPageWidth() - (this.template.pageMarginRight + this.template.pageMarginLeft) + this.template.sizeUnit,
      height: this.getPageHeight() - (this.template.pageMarginTop + this.template.pageMarginBottom) + this.template.sizeUnit,
      position: 'absolute',
      marginTop: this.template.pageMarginTop + this.template.sizeUnit,
      marginRight: this.template.pageMarginRight + this.template.sizeUnit,
      marginBottom: this.template.pageMarginBottom + this.template.sizeUnit,
      marginLeft: this.template.pageMarginLeft + this.template.sizeUnit,
      border: '1px solid #a0a0a0',
    };
  }

  public getHeaderStyle(headerObj: ReportElement, page: ReportPage): ElementsStyleObject {
    return {
      height: headerObj.height + this.template.sizeUnit,
      position: 'relative',
      visibility: page.hasHeader ? 'visible' : 'hidden',
    };
  }

  public getFooterStyle(footerObj: ReportElement, page: ReportPage): ElementsStyleObject {
    const marginVal =
      this.getPageHeight() -
      (this.template.pageMarginTop + this.template.pageMarginBottom) -
      this.template.header.height -
      this.template.footer.height;
    return {
      height: footerObj.height + this.template.sizeUnit,
      position: 'relative',
      marginTop: marginVal + this.template.sizeUnit,
      visibility: page.hasFooter ? 'visible' : 'hidden',
    };
  }

  public getTableContainerStyle(table: TemplateMeasurementPoint): ElementsStyleObject {
    return {
      fontSize: table.fontSize + this.template.sizeUnit,
      fontFamily: `${table.fontFamily} !important`,
      left: '0mm',
      top: '10mm',
    };
  }

  public getTableStyle(table: TemplateMeasurementPoint, index: number): ElementsStyleObject {
    let marginValue = 0;
    if (this.measurementPointPageBreakInfo && this.measurementPointPageBreakInfo.length > 0) {
      const element = this.measurementPointPageBreakInfo.find(el => el.index === index);
      marginValue = element ? element.marginTop : 0;
    }
    return {
      'table-layout': 'fixed',
      width: '100%',
      border: `${table.tableBorderWidth}${this.template.sizeUnit} ${table.tableBorderStyle} ${table.tableBorderColor}`,
      marginTop: marginValue + this.template.sizeUnit,
    };
  }

  public getTableAttributeStyleValue(input: string): string {
    return `${input}mm`;
  }

  public getVisualEventContainerStyle(visualEvent: TemplateVisualEvents, index: number): ElementsStyleObject {
    const dimensions = this.getVEContainerDimensions(visualEvent, index);
    //calculate how many lines needed for a long description
    const linesForLongDescription = this.calculateDescriptionLengthInMM(this.visualEventData[0].description) / dimensions.width;

    return {
      'min-height': dimensions.height + linesForLongDescription * 5 + this.template.sizeUnit,
      width: dimensions.width + this.template.sizeUnit,
      margin: '2mm',
    };
  }
  public getPhotoGalleryContainerStyle(photoGallery: TemplateVisualEvents, index: number): ElementsStyleObject {
    const dimensions = this.getPGContainerDimensions(photoGallery, index);
    return {
      height: dimensions.height + this.template.sizeUnit,
      width: dimensions.width + this.template.sizeUnit,
      margin: '2mm',
    };
  }
  public getDimensionValue(value: number): number {
    return value;
  }

  public getVisualEventImageStyle(visualEvent: TemplateVisualEvents): ElementsStyleObject {
    return {
      width: 92 + this.template.sizeUnit,
      height: 68 + this.template.sizeUnit,
      margin: '2mm',
    };
  }
  public getPhotoGalleyImageStyle(visualEvent: TemplateVisualEvents): ElementsStyleObject {
    return {
      width: 92 + this.template.sizeUnit,
      height: 68 + this.template.sizeUnit,
      margin: '2mm',
      objectFit: 'contain',
    };
  }

  public getVisualEventImageContainerStyle(visualEvent: TemplateVisualEvents, index: number): ElementsStyleObject {
    const dimensions = this.getVEContainerDimensions(visualEvent, index);

    return {
      height: dimensions.height - 5 * visualEvent.captionsList.length + this.template.sizeUnit,
      width: dimensions.width + this.template.sizeUnit,
    };
  }

  public getPhotoGalleryImageContainerStyle(photoGallery: TemplateVisualEvents, index: number): ElementsStyleObject {
    const dimensions = this.getPGContainerDimensions(photoGallery, index);

    return {
      height: dimensions.height - 10 + this.template.sizeUnit,
      width: dimensions.width + this.template.sizeUnit,
    };
  }

  public getPageHeight(): number {
    switch (this.template.paperSize) {
      case 'Letter':
        return LETTER_PAGE_HEIGHT;
      case 'Legal':
        return LEGAL_PAGE_HEIGHT;
      case 'A4':
        return A4_PAGE_HEIGHT;
      case 'B5':
        return B5_PAGE_HEIGHT;
    }
  }

  public getPageWidth(): number {
    switch (this.template.paperSize) {
      case 'Letter':
        return LETTER_PAGE_WIDTH;
      case 'Legal':
        return LEGAL_PAGE_WIDTH;
      case 'A4':
        return A4_PAGE_WIDTH;
      case 'B5':
        return B5_PAGE_WIDTH;
    }
  }
  public async getConfigDataRelatedToReport(): Promise<{
    redLines: Array<WhiteboardUpdate[]>;
    measurements: Array<Measurement>;
    imageUris: Array<string>;
    imageIsoMetaInfo: Array<ImageInfo>;
    redlineImages: Array<string>;
    alarmConfigs: Array<AlarmConfig>;
  }> {
    const taskImages = this.#reportService.taskImages();
    const measurements: Array<Measurement> = [];
    const alarmConfigs: Array<AlarmConfig> = [];
    const redLines: Array<WhiteboardUpdate[]> = [];
    const imageUris: Array<string> = [];
    const imageIsoMetaInfo: Array<ImageInfo> = [];
    const redlineImages: Array<string> = [];
    this.allVisualEventData = [];
    this.photoEventsData = [];
    const promises = taskImages.map(({ metadataFile, imageFile }) => this.fetchISOdata(metadataFile.id, imageFile));
    const data = await Promise.all(promises);

    data.forEach((item: any[], index: number) => {
      this.veISOLoaded[index] = false;
      this.cmlISOLoaded[index] = false;
      redLines[index] = item[0] as WhiteboardUpdate[];
      measurements[index] = item[1] as Measurement;
      imageUris[index] = item[2];
      imageIsoMetaInfo.push({ fileId: taskImages[index].imageFile.id, imageUrl: taskImages[index].imageFile.downloadUri });
      redlineImages.push(item[2]);
      if (this.inspectionType === InspectionTypes.UT || this.inspectionType === InspectionTypes.UT_Baseline) {
        alarmConfigs.push(item[3]);
      }
    });

    return { redLines, measurements, imageUris, imageIsoMetaInfo, redlineImages, alarmConfigs };
  }

  public fetchISOdata(ISOId: string, image: FileInfo) {
    const promises = [];
    promises.push(firstValueFrom(this.#reportService.getRedlines(ISOId, this.taskId)));
    promises.push(this.isMobile ? firstValueFrom(this.#fetcher.load(ISOId)) : firstValueFrom(this.#fetcher.fetch(ISOId)));
    promises.push(this.#ionicService.getImageUri(image));
    if (this.inspectionType === InspectionTypes.UT || this.inspectionType === InspectionTypes.UT_Baseline) {
      promises.push(firstValueFrom(this.#reportService.getInteractiveIsometricAlarmConfiguration(ISOId)));
    }
    return Promise.all(promises);
  }

  public getTemplateBodies() {
    this.template.bodies.forEach((itm, pageIndex) => {
      itm.tables.forEach((element, tableIndex) => {
        const reportPipe = new ReportTablePipe();
        reportPipe.transform(element, this.allVisualEventData);
        if (element.sectionStageTag === 'VEList') {
          element['showStatus'] = false;
          element['showPriority'] = false;
          element['pageIndex'] = pageIndex;
          element['tableIndex'] = tableIndex;
          element['tableId'] = element.id;
          const statusItems = element.columnList.filter(item => item.tagging === 'StatusItem');
          const statusLabels = statusItems.map(item => item.label);
          if (statusItems.length > 0) {
            element['showStatus'] = true;
            element['statues'] = statusItems;
            element.rowList.forEach((item, rowIndex) => {
              statusLabels.forEach(status => {
                if (element.dataSource[rowIndex][status].data) {
                  item['selectedStatus'] = status;
                }
              });
            });
          }
          const priorityItem = element.columnList.filter(item => item.tagging === 'PriorityItem');
          if (priorityItem.length > 0) {
            element['showPriority'] = true;
            element.rowList.forEach((item, rowIndex) => {
              priorityItem.forEach(priority => {
                const pData = element.dataSource[rowIndex][priority.label].data;
                item['selectedPriority'] = pData?.length > 0 ? element.priorityList.find(x => x?.code.includes(pData))?.code : '';
                element['priorityColumnLabel'] = priority.label;
              });
            });
          }
        }
        if (element.tableType === 'surveyTable') {
          element.dataSource.forEach(row => {
            row.isShow = false;
          });
        }
      });

      itm.texts?.forEach(text => {
        text.editMode = false;
        if (
          text.type === 'dynamic-editable' &&
          (text.inputType === 'text' || text.inputType === 'select' || text.inputType === 'multiSelect' || text.inputType === 'textArea')
        ) {
          this.getDynamicEditableText(text);
          this.validateTextInput(text);
        }
      });
    });
  }

  public async getTemplateInfo(): Promise<void> {
    await this.#reportService.loadTaskFiles(this.projectId, this.jobId, this.taskId);
    this.templateFile = this.#reportService.inspectionReportFile();
    if (this.templateFile?.downloadUri) {
      const promises: [Promise<object>, Promise<any>, Promise<TemplateOfflineImage[]>] = [
        firstValueFrom(this.#reportService.getTemplate(this.templateFile)),
        lastValueFrom(this.#reportService.getData(this.projectId, this.jobId, this.taskId)),
        firstValueFrom(this.#reportService.getTemplateImages(this.taskId)),
      ];

      Promise.all(promises)
        .then(async ([fileContent, inspectionReportData, offlineImages]) => {
          this.template = fileContent;
          this.#originalTemplate = structuredClone(this.template);
          const reportData = await this.getConfigDataRelatedToReport();
          this.template.bodies.forEach(itm => {
            const textItems = [];
            itm.texts?.forEach(element => {
              const textJson = { id: element.id, type: element.type, initialText: element.text, inputType: element.inputType };
              textItems.push(textJson);
            });
            const finalTextJson = { texts: textItems };
            this.initialTemplate.push(finalTextJson);
          });
          reportData.measurements.forEach(itm => itm.points.sort((obj1, obj2) => obj1.label.localeCompare(obj2.label)));
          this.measurementData = reportData.measurements;
          this.isoImageUris = reportData.imageUris;
          this.isoImageMetaInfo = reportData.imageIsoMetaInfo;
          this.redlineImageData = reportData.redlineImages;
          this.offlineImages = offlineImages;
          this.alarmConfig = reportData.alarmConfigs;
          this.allVisualEventData = this.visualEvents.filter(x => !x.isPhotoOnlyEvent);
          this.photoEventsData = this.getPhotoEventsNotLinkedToVisualEvents();
          this.splitVEData = this.splitVisualEventData(this.visualEvents.filter(x => !x.isPhotoOnlyEvent && x.images.length > 0));
          this.visualEventData = this.allVisualEventData.filter(x => x.images.length > 0);
          this.inspectionReportData = inspectionReportData;
          this.redlineData = structuredClone(reportData.redLines);
          this.inspectionReportData = {
            'visualEvent.numberOfEvents': this.visualEventData.length,
            'visualEvent.numberOfPhotos': this.visualEventData.reduce((acc, x) => acc + x.images.length, 0),
            ...this.inspectionReportData,
          };
          this.getTemplateBodies();
          this.getTemplateIso();
          this.alterTemplateForDynamicComponents();
          this.filteredPages = this.template.bodies.filter(page => !page.hidePage);
          this.changeDetectorRef.detectChanges();
          this.repaginateMeasurementPointsFromRenderedHeights();
          this.filteredPages = this.template.bodies.filter(page => !page.hidePage);
          this.#originalTemplate = structuredClone(this.template);
          this.scaleReport();
          this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
          this.changeDetectorRef.detectChanges();
        })
        .catch(err => {
          this.#logger.error('getting report info failed', err);
          this.#snackBarService.error(this.#translate.instant('measurementReport.getTemplateInfoWithError'));
          this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
          this.changeDetectorRef.detectChanges();
        });
    } else {
      this.#snackBarService.error(this.#translate.instant('measurementReport.getTemplateInfoNoForm'));
      this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
      this.changeDetectorRef.detectChanges();
    }
  }

  public alterTemplateForDynamicComponents(): void {
    this.template.bodies.map((page, index) => {
      if (page.measurementPoints && page.measurementPoints.length > 0 && page.pageType !== 'custom') {
        this.addPagesForMeasurementPoints(index);
      }
    });
    this.template.bodies.map((page, index) => {
      if (page.visualEvents && page.visualEvents.length > 0) {
        this.addPagesForVisualEvents(page.visualEvents[0], index);
      }
    });
    this.template.bodies.map((page, index) => {
      if (page.tables && page.tables.length > 0 && page.pageType !== 'custom') {
        this.addPagesForGenericTable(index);
      }
    });
    this.template.bodies.map((page, index) => {
      if (page.images && page.images.length > 0 && page.images[0].dynamicId === 'photoGallery') {
        this.addPagesForPhotoGallery(page.images[0], index);
      }
    });
  }

  public rowIdContainStar(rowData: SurveyMeasurement): boolean {
    return rowData?.id.includes('*');
  }

  public isCurrentMeasurementNotTaken(rowData: SurveyMeasurement): boolean {
    return rowData && rowData.measurement === -1;
  }

  public isCommentRequiredOrNot(rowData: SurveyMeasurement): boolean {
    return rowData && rowData.comment === '*required';
  }

  public isCheckDynamicEditable(rowData: any, dataField: any): boolean {
    if (rowData?.isCustom) {
      return false;
    }
    for (const [keyN, valueN] of Object.entries(rowData)) {
      if (keyN === dataField) {
        return valueN ? false : true;
      }
    }
    return true;
  }

  public replaceStar(text: string): string {
    return text.replace('*', '');
  }

  public isDynamicEditableValueUpdated(rowData: any, label: any): boolean {
    const measurementUserData = this.measurementData.map(item => item.points).flat();
    const id = rowData.id.split('-')[1];
    const rowDataId = rowData.id.split('*')[1] ? rowData.id.split('*')[1] : rowData.id;
    const point = measurementUserData.find(ids => ids.label + '-' + id === rowDataId);
    const idData = point?.ids.find(idD => point.label + '-' + idD.id === rowDataId);
    if (idData && idData[label] !== rowData[label]) {
      return true;
    }
    return false;
  }

  public addPagesForMeasurementPoints(pageIndex: number): void {
    const measurementPoints: TemplateMeasurementPoint = this.template.bodies[pageIndex].measurementPoints[0];
    measurementPoints.startRow = -1;
    measurementPoints.endRow = -1;
    measurementPoints.dataSourceIndex = pageIndex;
    if (!measurementPoints || measurementPoints.tableIndex) {
      return;
    }
    // Snapshot the saved rows before the table is rebuilt: the rebuild overwrites dataSource slot by slot, and page
    // indexes no longer match #originalTemplate once getTemplateIso has inserted pages for additional ISO drawings.
    if (measurementPoints.dataSource) {
      this.#savedMeasurementRows.set(pageIndex, structuredClone(measurementPoints.dataSource.flat()));
    } else {
      this.#savedMeasurementRows.delete(pageIndex);
    }
    const combinedMeasurementData: SurveyPoint[] = [];
    this.measurementData.forEach(itm => {
      combinedMeasurementData.push(...itm.points);
    });
    const measurementPointData = structuredClone(combinedMeasurementData);
    if (!measurementPointData) {
      return;
    }
    let totalWidthRatio = 0;

    measurementPoints.columns.forEach(column => {
      if (column.widthRatio !== undefined) {
        totalWidthRatio += column.widthRatio;
      }
    });

    const remainingRatio = 100 - totalWidthRatio;
    const widthRatioToAssign = remainingRatio / measurementPoints.columns.length;

    measurementPoints.columns.forEach(column => {
      if (column.widthRatio === undefined) {
        column.widthRatio = widthRatioToAssign;
      } else if (totalWidthRatio !== 100) {
        column.widthRatio += widthRatioToAssign;
      }
    });

    let availableSpace = this.getMeasurementPageAvailableSpace();
    let counter = 1;
    measurementPointData.forEach((el, index) => {
      const ids = el.ids;
      const measurementUserData = this.#savedMeasurementRows.get(pageIndex);
      if (measurementUserData) {
        const fields = this.template.bodies[pageIndex]?.measurementPoints[0].columns?.filter(itm => itm.type === 'Dynamic-Editable') ?? [];
        if (fields.length > 0) {
          ids.map(itm => {
            const rowId = this.getMeasurementRowId(el.label, itm);
            fields.forEach(field => {
              const msmnt = measurementUserData.find(d => d.id === rowId && d[field.dataField] !== itm[field.dataField]);
              if (msmnt) {
                itm[field.dataField] = msmnt[field.dataField];
              }
            });
          });
        }
      }
      const readings = ids;
      const rowItemHeights = [];
      let cumulativeRowItemHeight = 0;
      readings.forEach((row, rowIndex) => {
        const rowColumnHeights = [];
        measurementPoints.columns.forEach(column => {
          rowColumnHeights.push(this.calculateMeasurementRowHeight(el.label, row, column, measurementPoints));
        });
        rowItemHeights.push(Math.max(...rowColumnHeights) + 2 * measurementPoints.cellMargin + this.measurementTableRowSafetyBufferInMm);
        cumulativeRowItemHeight += rowItemHeights[rowItemHeights.length - 1];
      });
      const tableHeight = this.getMeasurementTableFixedHeight(measurementPoints) + cumulativeRowItemHeight;
      [counter, availableSpace] = this.checkTableRowsFitOnPage(
        availableSpace,
        tableHeight,
        readings,
        rowItemHeights,
        index,
        el,
        pageIndex,
        counter,
        0,
      );
    });
  }

  public checkTableRowsFitOnPage(
    availableSpace: number,
    tableHeight: number,
    readings: SurveyMeasurement[],
    rowItemHeights: number[],
    index: number,
    el: SurveyPoint,
    pageIndex: number,
    counter: number,
    tableRowStart: number,
    measurementPoints?: TemplateMeasurementPoint,
  ): [number, number] {
    if (measurementPoints === undefined) {
      measurementPoints = this.template.bodies[pageIndex + counter - 1].measurementPoints[0];
    }
    if (availableSpace - tableHeight < this.getDimensionValue(this.availableSpaceOffsetInMm)) {
      let splitIndex = readings.length;
      let tableAdjustedHeight = this.getMeasurementTableFixedHeight(measurementPoints);

      // find how many rows fit on page
      for (let i = tableRowStart; i < readings.length; i++) {
        tableAdjustedHeight += rowItemHeights[i];
        if (availableSpace - tableAdjustedHeight < this.availableSpaceOffsetInMm) {
          tableAdjustedHeight -= rowItemHeights[i];
          splitIndex = i;
          break;
        }
      }

      // If one row is taller than a full page, keep pagination moving by placing that row on its own page.
      if (splitIndex === tableRowStart && this.isFullMeasurementPageSpaceAvailable(availableSpace) && tableRowStart < readings.length) {
        tableAdjustedHeight += rowItemHeights[tableRowStart];
        splitIndex = tableRowStart + 1;
      }

      if (splitIndex >= readings.length) {
        availableSpace = availableSpace - tableHeight;
        this.addMeasurementPointTable(pageIndex, counter, index);
        this.addMeasurementPointDataSource(readings, measurementPoints, index, el, pageIndex, tableRowStart > 0);
        return [counter, availableSpace];
      }

      // add top rows of table to current page
      if (splitIndex > tableRowStart) {
        this.addMeasurementPointTable(pageIndex, counter, index);
        this.addMeasurementPointDataSource(readings, measurementPoints, index, el, pageIndex, tableRowStart > 0, splitIndex, true);
      }
      // add remaining rows to new page
      const measurementPoint = this.template.bodies[pageIndex].measurementPoints.map(item => Object.assign({}, item));
      measurementPoint[0].tableIndex = [index];
      const newPage = {
        pageType: 'custom',
        hasHeader: this.template.bodies[pageIndex].hasHeader,
        hasFooter: this.template.bodies[pageIndex].hasFooter,
        measurementPoints: measurementPoint,
      };
      this.template.bodies.splice(pageIndex + counter, 0, Object.assign({}, newPage));
      measurementPoints = this.template.bodies[pageIndex + counter].measurementPoints[0];
      measurementPoints.startRow = -1;
      measurementPoints.endRow = -1;
      counter++;
      this.addMeasurementPointDataSource(readings, measurementPoints, index, el, pageIndex, tableRowStart > 0, splitIndex, false);
      const newPageTableHeight =
        splitIndex > tableRowStart
          ? tableHeight - tableAdjustedHeight + this.getMeasurementTableFixedHeight(measurementPoints)
          : tableHeight;
      availableSpace = this.getMeasurementPageAvailableSpace() - newPageTableHeight;
      if (availableSpace < this.availableSpaceOffsetInMm) {
        availableSpace = this.getMeasurementPageAvailableSpace();
        const [updatedCounter, updatedAvailableSpace] = this.checkTableRowsFitOnPage(
          availableSpace,
          newPageTableHeight,
          readings,
          rowItemHeights,
          index,
          el,
          pageIndex,
          counter,
          splitIndex,
          measurementPoints,
        );
        return [updatedCounter, updatedAvailableSpace];
      }
    } else {
      // add table to current page
      availableSpace = availableSpace - tableHeight;
      this.addMeasurementPointTable(pageIndex, counter, index);
      this.addMeasurementPointDataSource(readings, measurementPoints, index, el, pageIndex, tableRowStart > 0);
    }
    return [counter, availableSpace];
  }

  public showMeasurementPointRow(
    measurementPoint: TemplateMeasurementPoint,
    rowIndex: number,
    tableIndex: number,
    tableIndexLength: number,
  ): boolean {
    if (measurementPoint.startRow !== -1 && tableIndex === 0) {
      if (measurementPoint.startRow > rowIndex) {
        return false;
      }
    }
    if (measurementPoint.endRow !== -1 && tableIndex === tableIndexLength - 1) {
      if (measurementPoint.endRow <= rowIndex) {
        return false;
      }
    }
    return true;
  }

  public addMeasurementPointTable(pageIndex: number, counter: number, index: number) {
    const table = this.template.bodies[pageIndex + (counter - 1)].measurementPoints[0];
    if (table?.tableIndex && table.tableIndex.findIndex(itm => itm === index) === -1) {
      table.tableIndex.push(index);
    } else {
      table.tableIndex = [index];
    }
  }
  public addMeasurementPointDataSource(
    readings: SurveyMeasurement[],
    measurementPoints: TemplateMeasurementPoint,
    index: number,
    el: SurveyPoint,
    pageIndex: number,
    recursiveCall?: boolean,
    splitIndex?: number,
    end?: boolean,
  ) {
    if (splitIndex) {
      if (end) {
        measurementPoints.endRow = splitIndex;
      } else {
        measurementPoints.startRow = splitIndex;
      }
    }
    if (((splitIndex && end) || !splitIndex) && !recursiveCall) {
      readings.forEach(row => {
        row.id = this.getMeasurementRowId(el.label, row);
        row.comment =
          (row.status === 'Skipped' || this.isMeasurementInAlarmState(row.measurement, row.previousMeasurement)) && !row.comment
            ? '*required'
            : row.comment;
        row.measurement = row.status !== 'Skipped' && row.measurement === 0 ? -1 : row.measurement;
      });
    }

    const savedRows = this.#savedMeasurementRows.get(pageIndex);
    measurementPoints.dataSource = measurementPoints.dataSource || [];
    measurementPoints.dataSource[index] = readings.map(row => {
      // Saved slots are positional, so a CML added since the last save shifts every following CML into another
      // CML's slot; fall back to the snapshot, where the row id (CML label + reading id) still identifies the row.
      const cached = measurementPoints.dataSource[index]?.find(p => p.id === row.id) ?? savedRows?.find(p => p.id === row.id);
      const newDataSource = cached ? { ...row, ...cached } : row;
      // MUST PRESERVE THE STATUS FOR CHECKING THE CONDITION IN HTML TO SHOW THE WARNING ICON, SO ADDING STATUS BACK TO NEW DATASOURCE OBJECT
      // MIGHT BE MISSING OTHER PROPERTIES
      return { ...newDataSource, status: row.status };
    });

    readings.forEach(row => {
      let dataRow = measurementPoints.dataSource[index].find(p => p.id === row.id);

      if (!dataRow) {
        dataRow = {};
        measurementPoints.dataSource[index].push(dataRow);
      }

      measurementPoints.columns.forEach(column => {
        if (column.type !== 'Editable') {
          // TODO : remove this condition after implementation of update dynamic editable data api
          if (savedRows) {
            if (column.type !== 'Dynamic-Editable') {
              dataRow[column.dataField] = row[column.dataField];
            } else {
              dataRow['allowEdit'] = undefined;
            }
          } else {
            dataRow['allowEdit'] = false;
            dataRow[column.dataField] = row[column.dataField];
          }
        } else if (!Object.hasOwn(dataRow, column.label)) {
          dataRow[column.label] = '';
        }
      });
    });

    const difference = measurementPoints.dataSource[index].filter((x: any) => !readings.some((r: any) => r.id === x.id));
    difference.forEach((item: any) => {
      const i = measurementPoints.dataSource[index].findIndex(p => p.id === item.id);
      measurementPoints.dataSource[index].splice(i, 1);
    });

    measurementPoints.dataSource[index].sort((a, b) => 0 - (a.id > b.id ? -1 : 1));
  }
  public addPagesForGenericTable(pageIndex: number): void {
    if (this.template.bodies[pageIndex].tables[0].dynamicDataField === 'visualEvents') {
      let tableData;
      if (
        this.template.bodies[pageIndex].tables[0].columnList.some(
          col => col.dataField === 'visualEventRecommendation' || col.dataField === 'description',
        )
      ) {
        tableData = this.allVisualEventData.filter(ve => ve.description && ve.description !== '' && ve.visualEventStatus !== 'N/A');
      } else {
        tableData = this.allVisualEventData;
      }
      tableData = tableData.sort((a, b) => this.sortVisualEvents(a, b, 'visualEventType'));

      if (this.template.bodies[pageIndex].tables[0].dynamicDataField === 'visualEvents') {
        tableData = tableData.map(x => {
          x.label = '00' + x.label;
          return x;
        });
      }
      if (!tableData || tableData.length === 0) {
        this.template.bodies[pageIndex].tables[0].dataSource = [];
        return;
      }

      if (this.template.bodies[pageIndex].tables[0].tableType !== 'genericTable') {
        return;
      }

      const availableSpace =
        this.getPageHeight() -
        (this.getDimensionValue(this.template.pageMarginTop) + this.getDimensionValue(this.template.pageMarginBottom)) -
        (this.getDimensionValue(this.template.header.height) + this.getDimensionValue(this.template.footer.height)) -
        10;

      const tableConfig = this.template.bodies[pageIndex].tables[0];
      const tableY = this.getDimensionValue(tableConfig.y ?? 0);
      const fontSize = tableConfig.fontSize ?? 3;
      const theadHeight = 1.35 * fontSize;
      const pageCapacity = availableSpace - tableY - theadHeight;
      const columnList: { width: number; dataField: string }[] = tableConfig.columnList ?? [];
      const charWidthMm = 0.5 * fontSize;
      const lineHeightMm = 1.35 * fontSize;

      const pages = this.createGenericTablePages(tableData, pageCapacity, columnList, charWidthMm, lineHeightMm);

      this.template.bodies[pageIndex].tables[0].dataSource = pages[0] ?? [];
      let counter = 1;
      for (let i = 1; i < pages.length; i++) {
        const table = this.template.bodies[pageIndex].tables.map(item => Object.assign({}, item));
        table[0].dataSource = pages[i];
        const newPage = {
          pageType: 'custom',
          hasHeader: this.template.bodies[pageIndex].hasHeader,
          hasFooter: this.template.bodies[pageIndex].hasFooter,
          tables: table,
        };
        this.template.bodies.splice(pageIndex + counter, 0, Object.assign({}, newPage));
        counter++;
      }
    } else if (this.template.bodies[pageIndex].tables[0].dynamicDataField === 'signature') {
      if (this.inspectionReportData.signatureInfoList) {
        this.template.bodies[pageIndex].tables[0].dataSource = this.inspectionReportData.signatureInfoList;
        this.saveTemplate();
      }
    }
  }

  public addPagesForVisualEvents(visualEvents: TemplateVisualEvents, pageIndex: number): void {
    if (!visualEvents || visualEvents.visualEventIndex) {
      return;
    }
    if (this.splitVEData.length === 0) {
      return;
    }

    let availableSpace =
      this.getPageHeight() -
      (this.getDimensionValue(this.template.pageMarginTop) + this.getDimensionValue(this.template.pageMarginBottom)) -
      (this.getDimensionValue(this.template.header.height) + this.getDimensionValue(this.template.footer.height)) -
      10;
    let counter = 1;
    let inlineSpaceAvailable = false;

    this.splitVEData.forEach((el, index) => {
      const visualEvent = this.template.bodies[pageIndex].visualEvents.map(item => ({ ...item }));
      const imageCount = el[_.camelCase(visualEvent[0].itemDataSource)]
        ? Math.min(el[_.camelCase(visualEvent[0].itemDataSource)].length, 6)
        : 0;
      let requiredVeSpace = 0;
      if (imageCount >= 2 && imageCount <= 6) {
        requiredVeSpace = this.getVEContainerDimensions(visualEvents, index).height + 4;
        inlineSpaceAvailable = false;
      } else {
        //execute this for one image or more than 6 images
        requiredVeSpace = !inlineSpaceAvailable ? this.getVEContainerDimensions(visualEvents, index).height + 4 : 0;
        inlineSpaceAvailable = !inlineSpaceAvailable;
      }
      if (availableSpace - requiredVeSpace < 0) {
        visualEvent[0].visualEventIndex = [index];
        const newPage = {
          pageType: 'custom',
          hasHeader: this.template.bodies[pageIndex].hasHeader,
          hasFooter: this.template.bodies[pageIndex].hasFooter,
          visualEvents: visualEvent,
        };
        this.template.bodies.splice(pageIndex + counter, 0, { ...newPage });
        availableSpace =
          this.getPageHeight() -
          (this.getDimensionValue(this.template.pageMarginTop) + this.getDimensionValue(this.template.pageMarginBottom)) -
          (this.getDimensionValue(this.template.header.height) + this.getDimensionValue(this.template.footer.height)) -
          10 -
          requiredVeSpace;
        counter++;
      } else {
        if (this.template.bodies[pageIndex + (counter - 1)].visualEvents[0]?.visualEventIndex) {
          this.template.bodies[pageIndex + (counter - 1)].visualEvents[0].visualEventIndex.push(index);
        } else {
          this.template.bodies[pageIndex + (counter - 1)].visualEvents[0].visualEventIndex = [index];
        }
        availableSpace = availableSpace - requiredVeSpace;
      }
    });
  }

  public addPagesForPhotoGallery(photoGallery: TemplateVisualEvents, pageIndex: number): void {
    if (!photoGallery || photoGallery.photoGalleryIndex) {
      return;
    }
    if (this.photoEventsData.length === 0) {
      return;
    }

    let availableSpace =
      this.getPageHeight() -
      (this.getDimensionValue(this.template.pageMarginTop) + this.getDimensionValue(this.template.pageMarginBottom)) -
      (this.getDimensionValue(this.template.header.height) + this.getDimensionValue(this.template.footer.height)) -
      10;
    let counter = 1;
    let inlineSpaceAvailable = false;

    this.photoEventsData.forEach((el, index) => {
      const image = this.template.bodies[pageIndex].images.map(item => ({ ...item }));
      const imageCount = el.images ? Math.min(el.images.length, 6) : 0;
      let requiredVeSpace = 0;
      if (imageCount === 1) {
        requiredVeSpace = !inlineSpaceAvailable ? this.getDimensionValue(75) : 0;
        inlineSpaceAvailable = !inlineSpaceAvailable;
      } else if (imageCount === 2) {
        requiredVeSpace = this.getDimensionValue(75);
        inlineSpaceAvailable = false;
      } else if (imageCount === 3 || imageCount === 4) {
        requiredVeSpace = this.getDimensionValue(145);
        inlineSpaceAvailable = false;
      } else if (imageCount === 5 || imageCount === 6) {
        requiredVeSpace = this.getDimensionValue(215);
        inlineSpaceAvailable = false;
      } else {
        requiredVeSpace = !inlineSpaceAvailable ? this.getDimensionValue(85) : 0;
        inlineSpaceAvailable = !inlineSpaceAvailable;
      }
      if (availableSpace - requiredVeSpace < 0) {
        image[0].photoGalleryIndex = [index];
        const newPage = {
          pageType: 'custom',
          hasHeader: this.template.bodies[pageIndex].hasHeader,
          hasFooter: this.template.bodies[pageIndex].hasFooter,
          images: image,
        };
        this.template.bodies.splice(pageIndex + counter, 0, { ...newPage });
        availableSpace =
          this.getPageHeight() -
          (this.getDimensionValue(this.template.pageMarginTop) + this.getDimensionValue(this.template.pageMarginBottom)) -
          (this.getDimensionValue(this.template.header.height) + this.getDimensionValue(this.template.footer.height)) -
          10 -
          requiredVeSpace;
        counter++;
      } else {
        if (this.template.bodies[pageIndex + (counter - 1)].images[0]?.photoGalleryIndex) {
          this.template.bodies[pageIndex + (counter - 1)].images[0].photoGalleryIndex.push(index);
        } else {
          this.template.bodies[pageIndex + (counter - 1)].images[0].photoGalleryIndex = [index];
        }
        availableSpace = availableSpace - requiredVeSpace;
      }
    });
  }

  /**
   *  Manage UI elements and bindings
   */
  public updateRadioInputs(tableId: string, tableIndex: number, rowIndex: number, row, column) {
    const pageIndex = (this.template.bodies as any[]).findIndex(b => b.tables.some(t => t.id === tableId));
    const columns = Object.keys(row);
    for (const label of columns) {
      if (row[label]?.row && row[label].column && row[label].inputType === 'radio') {
        let radioValue = false;
        if (label === column.label) {
          radioValue = true;
        }
        this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].column.data = radioValue;
        this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].isDataEntered = radioValue;
        this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].data = radioValue;
      }
    }
    this.checkChanges();
  }

  public loadVEData(filterOnlyLocationEvents: VisualEvent[], currentSelectedFileId: string): VisualEvent[] {
    const veFilterData: Array<VisualEvent> = [];
    filterOnlyLocationEvents.forEach(res => {
      const loc = res.locations.filter(l => l.fileId === currentSelectedFileId);
      if (loc.length > 0) {
        const data = {
          eventId: res?.eventId,
          taskId: res.taskId,
          label: res?.label,
          locations: loc,
          wholeEquipment: res?.wholeEquipment,
          visualEventType: res?.visualEventType,
          description: res.description,
          images: res.images,
          isPhotoOnlyEvent: res.isPhotoOnlyEvent,
          visualEventStatus: res?.visualEventStatus,
          visualEventPriority: res?.visualEventPriority,
          visualEventRecommendation: res?.visualEventRecommendation,
        };
        veFilterData.push(data);
      }
    });

    return veFilterData.filter(v => (v.locations ?? []).length > 0);
  }

  public getTemplateIso(): void {
    let pages: Array<any> = structuredClone(this.template.bodies);
    const utIndex = pages.findIndex(
      page => page.interactiveIso && page.interactiveIso.length > 0 && page.interactiveIso[0].isoType === 'UTT',
    );
    if (utIndex > -1) {
      if (pages[utIndex].interactiveIso.index) {
        const utPages = pages.filter(
          page => page.interactiveIso && page.interactiveIso.length > 0 && page.interactiveIso[0].isoType === 'UTT',
        );
        this.template.bodies.splice(utIndex + 1, utPages.length - 1);
      }
      const isoObject = structuredClone(pages[utIndex].interactiveIso[0]);
      this.redlineData.forEach((itm, index) => {
        const newIsoObject = structuredClone(isoObject);
        newIsoObject.index = index;
        if (index === this.redlineData.length - 1) {
          this.template.bodies[utIndex + index].interactiveIso[0] = newIsoObject;
        } else {
          const newPage = {
            pageType: 'custom',
            hasHeader: this.template.bodies[utIndex].hasHeader,
            hasFooter: this.template.bodies[utIndex].hasFooter,
            interactiveIso: [newIsoObject],
          };
          this.template.bodies.splice(utIndex + index, 0, { ...newPage });
        }
        this.cmls[index] = structuredClone(this.measurementData[index].points);
      });
      pages = structuredClone(this.template.bodies);
    }
    const vtIndex = pages.findIndex(
      page => page.interactiveIso && page.interactiveIso.length > 0 && page.interactiveIso[0].isoType === 'VisualEvent',
    );
    if (vtIndex > -1) {
      if (pages[vtIndex].interactiveIso.index) {
        const vtPages = pages.filter(
          page => page.interactiveIso && page.interactiveIso.length > 0 && page.interactiveIso[0].isoType === 'VisualEvent',
        );
        this.template.bodies.splice(vtIndex + 1, vtPages.length - 1);
      }
      const isoObject = structuredClone(pages[vtIndex].interactiveIso[0]);
      const metadataId = this.#reportService.taskImages().map(({ metadataFile }) => metadataFile.id);

      this.redlineData.forEach(async (itm, index) => {
        const newIsoObject = structuredClone(isoObject);
        newIsoObject.index = index;
        const filterOnlyLocationEvents = this.visualEvents.filter(
          v => !v.isPhotoOnlyEvent && v.taskId === this.taskId && (v.locations ?? []).length > 0,
        );

        this.visualEventsMarkers[index] = this.loadVEData(filterOnlyLocationEvents, metadataId[index]);

        if (index === this.redlineData.length - 1) {
          this.template.bodies[vtIndex + index].interactiveIso[0] = newIsoObject;
        } else {
          const newPage = {
            pageType: 'custom',
            hasHeader: this.template.bodies[vtIndex].hasHeader,
            hasFooter: this.template.bodies[vtIndex].hasFooter,
            interactiveIso: [newIsoObject],
          };
          this.template.bodies.splice(vtIndex + index, 0, { ...newPage });
        }
        this.template.bodies[vtIndex + index].hidePage = this.visualEventsMarkers[index].length === 0;
      });
      pages = structuredClone(this.template.bodies);
    }
    const redlinePageIndex = pages.findIndex(
      page => page.interactiveIso && page.interactiveIso.length > 0 && page.interactiveIso[0].isoType === 'Redline',
    );
    if (redlinePageIndex > -1) {
      if (pages[redlinePageIndex].interactiveIso.redlineData) {
        const redlinePages = pages.filter(
          page => page.interactiveIso && page.interactiveIso.length > 0 && page.interactiveIso[0].isoType === 'Redline',
        );
        this.template.bodies.splice(redlinePageIndex + 1, redlinePages.length - 1);
      }
      const isoObject = structuredClone(pages[redlinePageIndex].interactiveIso[0]);

      this.redlineData.forEach((itm, index) => {
        const newIsoObject = structuredClone(isoObject);
        newIsoObject.redlineData = structuredClone(itm);
        newIsoObject.aspectRatio = WhiteboardHelper.getAspectRatio(
          this.measurementData[index].image.height,
          this.measurementData[index].image.width,
        );
        newIsoObject.index = index;

        if (index === this.redlineData.length - 1) {
          this.template.bodies[redlinePageIndex + index].interactiveIso[0] = newIsoObject;
        } else {
          const newPage = {
            pageType: 'custom',
            hasHeader: this.template.bodies[redlinePageIndex].hasHeader,
            hasFooter: this.template.bodies[redlinePageIndex].hasFooter,
            interactiveIso: [newIsoObject],
          };
          this.template.bodies.splice(redlinePageIndex + index, 0, { ...newPage });
        }
        this.template.bodies[redlinePageIndex + index].hidePage = itm.length === 0;
      });
      pages = structuredClone(this.template.bodies);
    }
  }
  public redlineImageLoaded(data: WhiteboardUpdate[], index: number) {
    const options: DrawingOptions = {
      shouldFillShape: true,
      strokeStyle: 'rgba(255, 0, 0, 1)',
      fillStyle: 'rgba(255, 0, 0, 0)',
      lineWidth: 1,
      lineCap: 'round',
      lineJoin: 'round',
    };
    const canvasElement = document.getElementById('redlineCanvas' + index) as HTMLCanvasElement;
    const redlineImage = document.getElementById('redlineImg' + index) as HTMLImageElement;
    this.#canvasService.getCanvas(canvasElement, canvasElement.scrollHeight, canvasElement.scrollWidth, data, options, redlineImage, false);
    setTimeout(() => {
      const redlineIndex = this.template.bodies.findIndex(
        page => page.interactiveIso && page.interactiveIso.length > 0 && page.interactiveIso[0].isoType === 'Redline',
      );
      this.getUpdatedIsoImage(this.template.bodies[redlineIndex + index].interactiveIso[0], index);
    }, 2000);
  }

  public getUpdatedIsoImage(image: ReportElement, index: number): void {
    this.updatedIsoImage[index] = (document.getElementById('redlineCanvas' + index) as HTMLCanvasElement)?.toDataURL();
    this.isIsoLoading = false;
    this.changeDetectorRef.markForCheck();
  }

  public veImageLoaded(index: number) {
    const image = document.getElementById('veImg' + index) as HTMLImageElement;
    const imageContainer = document.getElementById('veImgContainer' + index);
    this.veOffsets[index] = {
      width: (imageContainer.offsetWidth - image.width) / 2,
      height: (imageContainer.offsetHeight - image.height) / 2,
    };
    if (!this.veISOLoaded[index]) {
      this.visualEventsMarkers[index].forEach(itm => {
        itm.locations.forEach(location => {
          location.left = location.left * this.zoomFactorX(image, index) - 20;
          location.top = location.top * this.zoomFactorY(image, index) - 20;
        });
      });
    }
    this.veISOLoaded[index] = true;
  }
  public cmlImageLoaded(index: number) {
    const image = document.getElementById('cmlImg' + index) as HTMLImageElement;
    const imageContainer = document.getElementById('cmlImgContainer' + index);
    this.cmlBubbleHeight = Math.sqrt(image.height * image.width) * staticStylesForIpmReport.cmlCircleSizeRelativeToImage;

    this.cmlOffsets[index] = {
      width: (imageContainer.offsetWidth - image.width) / 2,
      height: (imageContainer.offsetHeight - image.height) / 2,
    };
    if (!this.cmlISOLoaded[index]) {
      this.cmls[index].forEach(itm => {
        itm.location.left = itm.location.left * this.zoomFactorX(image, index) - this.cmlBubbleHeight / 2;
        itm.location.top = itm.location.top * this.zoomFactorY(image, index) - this.cmlBubbleHeight / 2;
      });
    }
    this.cmlISOLoaded[index] = true;
  }

  public enableEditMode(element: ReportElement) {
    if (!this.previewMode && !this.linkData) {
      element.editMode = true;
      element.isEmpty = false;
      this.focusElements();
    } else if (this.linkData && !this.previewMode) {
      element.dynamicBinding = this.linkData;
      this.linkData = '';
    }
  }

  public async takeReportSnapshot(imageElement: ReportElement): Promise<void> {
    if (this.previewMode) {
      return;
    }
    const { projectId, jobId, taskId } = this;
    const originalFileId = imageElement.id;
    try {
      const taskFile = await firstValueFrom(this.#reportService.addImage(projectId, jobId, taskId, originalFileId));
      imageElement.id = taskFile.id;
      imageElement['src'] = taskFile.downloadUri;
      // HACK: We need to sync cached offline images with newly added one.
      if (this.isMobile) {
        const offlineImage = this.offlineImages.find(i => i.imageId === originalFileId);
        if (offlineImage) {
          offlineImage.uri = taskFile.downloadUri;
          offlineImage.imageId = taskFile.id;
        } else {
          this.offlineImages.push({ taskId, imageId: taskFile.id, uri: taskFile.downloadUri });
        }
      }
      await this.saveTemplate();
      this.changeDetectorRef.markForCheck();
      this.checkChanges();
    } catch (err) {
      this.#logger.error('addReportImage failed', err);
    }
  }

  public bindData(element: ReportElement): void {
    if (this.linkData && !this.previewMode) {
      element.dynamicId = this.linkData;
      this.linkData = '';
    }
  }

  public disableEditMode(element: ReportElement) {
    element.editMode = false;
  }

  public getDynamicText(dynamicDataField: string, dynamicID?: string, format?: string, pageNumber?: number, pageCount?: number) {
    if (dynamicID && dynamicID === 'pageNumber') {
      return format.replace('$', (pageNumber + 1).toString()).replace('#', pageCount.toString());
    } else {
      if (this.inspectionReportData) {
        return dynamicDataField ? this.inspectionReportData[dynamicDataField] : dynamicID ? this.inspectionReportData[dynamicID] : '';
      }

      return '';
    }
  }

  public getDynamicEditableText(text: any) {
    // Inject the dynamic default only the first time the field is initialized.
    // Once applied, the flag is persisted with the template, so an empty value
    // afterwards is a deliberate user deletion and must be preserved instead of
    // being re-defaulted.
    if (text.dynamicDataField && !text.defaultApplied) {
      text.defaultApplied = true;
      if (text.text === '') {
        text.text = this.inspectionReportData[text.dynamicDataField];
      }
    }
    return text.text;
  }

  public getOfflineImage(image): string {
    return this.offlineImages.find(i => i.imageId === image.id)?.uri ?? image.src;
  }

  /**
   * Component styles
   */
  public getMeasurementRowStyle(table: TemplateMeasurementPoint, evenRow: boolean, rowData: SurveyMeasurement): ElementsStyleObject {
    return {
      backgroundColor: evenRow && table.isStriped ? 'rgba(0, 0, 0, 0.05)' : null,
      color: rowData && rowData.status === SurveyMeasurementStatuses.CompleteWithAlarm ? '#b30000' : null,
      fontWeight: rowData && rowData.status === SurveyMeasurementStatuses.CompleteWithAlarm ? 'bold' : null,
    };
  }

  public getVEContainerDimensions(visualEvent: TemplateVisualEvents, index: number): { width: number; height: number } {
    const imageCount: number = this.getVisualEventItem(visualEvent, index)?.length;
    const availableWidth: number =
      this.getPageWidth() - (this.getDimensionValue(this.template.pageMarginLeft) + this.getDimensionValue(this.template.pageMarginRight));
    const retval = {
      width: (availableWidth - this.getDimensionValue(10)) / 2,
      height: this.getDimensionValue(70 + visualEvent.captionsList.length * 5),
    };
    if (imageCount > 1) {
      retval.width = availableWidth - this.getDimensionValue(5);
      switch (imageCount) {
        case 3:
        case 4: {
          retval.height = this.getDimensionValue(150 + visualEvent.captionsList.length * 5);
          break;
        }
        case 5:
        case 6: {
          retval.height = this.getDimensionValue(230 + visualEvent.captionsList.length * 5);
          break;
        }
      }
    }
    return retval;
  }

  public getPGContainerDimensions(photoGallery: TemplateVisualEvents, index: number): { width: number; height: number } {
    const galleryItem = this.getPhotoGalleryItem(index);

    if (galleryItem == null) {
      return;
    }

    const imageCount = galleryItem.images?.length;

    const availableWidth =
      this.getPageWidth() - (this.getDimensionValue(this.template.pageMarginLeft) + this.getDimensionValue(this.template.pageMarginRight));
    const retval = {
      width: (availableWidth - this.getDimensionValue(10)) / 2,
      height: this.getDimensionValue(80),
    };
    if (imageCount > 1) {
      retval.width = availableWidth - this.getDimensionValue(5);
      switch (imageCount) {
        case 3:
        case 4: {
          retval.height = this.getDimensionValue(160);
          break;
        }
        case 5:
        case 6: {
          retval.height = this.getDimensionValue(240);
          break;
        }
      }
    }
    return retval;
  }

  public getDropDownStyle(dropDownList): DropDownElementStyleObject {
    return {
      width: '100%',
      height: '100%',
      backgroundColor: 'transparent',
      whiteSpace: 'normal',
      display: 'flex',
      flexDirection: 'column',
      MozAppearance: 'none',
      WebkitAppearance: 'none',
      border: '0',
    };
  }

  public getColumnStyle(column) {
    if (column.textOrientation === 'vertical') {
      return this.staticStyles.uprightText;
    }
  }

  public getVisualEventItem(visualEvent: TemplateVisualEvents, index: number): ImageInfo[] {
    const veData = this.splitVisualEventData(this.visualEvents.filter(x => !x.isPhotoOnlyEvent && x.images.length > 0));
    // imageUrls is coming from the report template, changed to images since we updated IPM implementation
    visualEvent.itemDataSource = visualEvent.itemDataSource === 'ImageUrls' ? 'images' : visualEvent.itemDataSource;
    const images: ImageInfo[] = veData[index] ? structuredClone(veData[index][_.camelCase(visualEvent.itemDataSource)]) : [];
    return images;
  }

  public getPhotoGalleryItem(index: number): VisualEvent | null {
    const pgData: any[] = this.visualEvents.filter(x => x.isPhotoOnlyEvent);
    return pgData.length > 0 ? structuredClone(pgData[index]) : null;
  }

  public getVisualEventCaption(caption: ListItem, index: number, splitImages = false): string {
    let veData: VisualEvent[] = this.visualEvents.filter(x => !x.isPhotoOnlyEvent && x.images.length > 0);
    veData = splitImages ? this.splitVisualEventData(veData) : veData;
    if (veData[index].images.length && caption.dataField === 'VisualEventType') {
      const veEventTypeStr = veData[index][_.camelCase(caption.dataField)].split('.');
      const veEventTypeCaption = `${veEventTypeStr[0]} ${'. '} ${veEventTypeStr[1]}`;
      return veEventTypeCaption;
    }
    return veData[index] ? structuredClone(veData[index][_.camelCase(caption.dataField)]) : '';
  }
  /**
   * CML Management
   */
  public getCmlStyles(cml: SurveyPoint) {
    const cmlStyle = this.staticStyles.cml;
    cmlStyle['background-color'] = this.getCmlBackground(cml);
    cmlStyle.border = this.getCmlBorder(cml);
    return cmlStyle;
  }

  public zoomFactorX(image: HTMLImageElement, index: number): number {
    return image.width / this.measurementData[index].image.width;
  }

  public zoomFactorY(image: HTMLImageElement, index: number): number {
    return image.height / this.measurementData[index].image.height;
  }

  public getMeasurementPointData(measurementPoint: TemplateMeasurementPoint, tableIndex: number): any[] {
    return this.template.bodies[measurementPoint.dataSourceIndex]?.measurementPoints[0]?.dataSource[tableIndex];
  }

  public isInAlarmState(surveyPoint: SurveyPoint): boolean {
    return (surveyPoint.ids ?? []).some(id =>
      MeasurementHelper.isMeasurementInAlarmState(id.measurement, id.previousMeasurement, this.alarmConfig[0]),
    );
  }

  public isMeasurementInAlarmState(measurement: number, previousMeasurement: number): boolean {
    return MeasurementHelper.isMeasurementInAlarmState(measurement, previousMeasurement, this.alarmConfig[0]);
  }

  public getCmlBorder(surveyPoint: SurveyPoint): string {
    if (this.isInAlarmState(surveyPoint)) {
      return `2px solid #b30000`;
    }
    switch (surveyPoint.measurementStatus) {
      case SurveyMeasurementStatuses.Complete: {
        return `1px solid #97d21e`;
      }
      case SurveyMeasurementStatuses.InProgress: {
        return `1px solid #E97D0F`;
      }
      case SurveyMeasurementStatuses.UnAssigned:
      case SurveyMeasurementStatuses.Pending: {
        return `1px solid #069BE3`;
      }
    }
  }

  public getCmlBackground(surveyPoint: SurveyPoint): string {
    switch (surveyPoint.measurementStatus) {
      case SurveyMeasurementStatuses.CompleteWithAlarm:
      case SurveyMeasurementStatuses.Complete: {
        return 'rgba(151, 210, 30, 0.5)';
      }
      case SurveyMeasurementStatuses.InProgress: {
        return 'rgba(233, 125, 15, 0.5)';
      }
      case SurveyMeasurementStatuses.UnAssigned:
      case SurveyMeasurementStatuses.Pending: {
        return 'rgba(6, 155, 227, 0.2)';
      }
    }
  }

  // TODO Verify if this method is still in use
  public removeSignatureDetailsInTemplate(): Promise<void> {
    this.template.signatureDetails = [];
    this.template.bodies.forEach(body => {
      body.tables?.forEach(table => {
        if (table.dynamicDataField === 'signature') {
          if (table.dataSource) {
            table.dataSource = [];
          }
        }
      });
    });
    this.changeDetectorRef.markForCheck();
    return Promise.resolve();
  }

  public addSignatureDetailsInTemplate(base64Signature: string, isInspector: boolean, isApprover: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const signatures: TemplateSignature[] = this.template.signatureDetails ? this.template.signatureDetails : [];
      if (!this.currentDateTime) {
        this.currentDateTime = new Date().toLocaleString();
      }
      if (isInspector) {
        if (signatures.some(signature => signature.signee === AuthorizedSignatory.Inspector)) {
          this.template.signatureDetails = signatures.map(el => {
            if (el.signee === AuthorizedSignatory.Inspector) {
              el.signature = base64Signature;
              el.user = this.userFullName;
              el.signatureDate = this.currentDateTime;
            }
            return el;
          });
        } else {
          signatures.push({
            signature: base64Signature,
            user: this.userFullName,
            signatureDate: this.currentDateTime,
            signee: AuthorizedSignatory.Inspector,
          });
          this.template.signatureDetails = signatures;
        }
      } else if (isApprover) {
        if (signatures.some(signature => signature.signee === AuthorizedSignatory.Reviewer)) {
          this.template.signatureDetails = signatures.map(el => {
            if (el.signee === AuthorizedSignatory.Reviewer) {
              el.signature = base64Signature;
              el.user = this.userFullName;
              el.signatureDate = this.currentDateTime;
            }
            return el;
          });
        } else {
          signatures.push({
            signature: base64Signature,
            user: this.userFullName,
            signatureDate: this.currentDateTime,
            signee: AuthorizedSignatory.Reviewer,
          });
          this.template.signatureDetails = signatures;
        }
      }
      this.getSignatureTemplateBody(base64Signature, isInspector, isApprover);
      this.changeDetectorRef.markForCheck();
      resolve();
    });
  }
  public getSignatureTemplateBody(base64Signature: string, isInspector: boolean, isApprover: boolean): void {
    this.template.bodies.forEach(body => {
      body.tables?.forEach(table => {
        if (table.dynamicDataField === 'signature') {
          const signee = isApprover ? AuthorizedSignatory.Reviewer : AuthorizedSignatory.Inspector;
          if (table.dataSource) {
            if (
              table.dataSource.some(
                signature =>
                  (signature.name === this.userFullName && signature.role === AuthorizedSignatory.Reviewer && isApprover) ||
                  (signature.name === this.userFullName && signature.role === AuthorizedSignatory.Inspector && isInspector),
              )
            ) {
              table.dataSource.map(el => {
                if (el.name === this.userFullName && el.role === AuthorizedSignatory.Reviewer && isApprover) {
                  el.signatureBase64 = base64Signature;
                  el.date = this.currentDateTime;
                }
                if (el.name === this.userFullName && el.role === AuthorizedSignatory.Inspector && isInspector) {
                  el.signatureBase64 = base64Signature;
                  el.date = this.currentDateTime;
                }
              });
            } else {
              this.#signatureList = table.dataSource;
            }
          }
          this.#signatureList.push({
            userId: this.userProfile.id,
            name: this.userFullName,
            signatureBase64: base64Signature,
            date: this.currentDateTime,
            role: signee,
          });
          this.inspectionReportData.signatureInfoList = this.#signatureList;
          this.template.bodies.map((page, index) => {
            if (page.tables && page.tables.length > 0 && page.pageType !== 'custom') {
              this.addPagesForGenericTable(index);
            }
          });
        }
      });
    });
  }
  public hasTemplateSignature(signature: ReportSignature): boolean {
    return this.template.signatureDetails ? this.template.signatureDetails.some(el => el.signee === signature.reservedFor) : false;
  }

  public getSignature(signature: ReportSignature): string {
    return this.template.signatureDetails ? this.template.signatureDetails.find(el => el.signee === signature.reservedFor).signature : '';
  }

  public getSigneeName(signature: ReportSignature): string {
    return this.template.signatureDetails ? this.template.signatureDetails.find(el => el.signee === signature.reservedFor).user : '';
  }

  public getSignatureDate(signature: ReportSignature): string {
    return this.template.signatureDetails
      ? this.template.signatureDetails.find(el => el.signee === signature.reservedFor).signatureDate
      : '';
  }

  public updateTextFields(tableId: string, tableIndex: number, rowIndex: number, row, column) {
    const pageIndex = (this.template.bodies as any[]).findIndex(b => b.tables.some(t => t.id === tableId));
    const columns = Object.keys(row);
    for (const label of columns) {
      if (row[label]?.row && row[label].column && (row[label].inputType === 'text' || row[label].inputType === 'textarea')) {
        if (label === column.label) {
          this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].allowEdit = false;
          if (this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].column.data) {
            this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].isDataEntered = true;
          } else {
            this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].isDataEntered = false;
          }
        }
      }
    }
  }

  public updateCheckboxOnMosueLeave(tableId: string, tableIndex: number, rowIndex: number, row, column) {
    const pageIndex = (this.template.bodies as any[]).findIndex(b => b.tables.some(t => t.id === tableId));
    const columns = Object.keys(row);
    for (const label of columns) {
      if (row[label]?.row && row[label].column && row[label].inputType === 'checkbox') {
        if (label === column.label) {
          if (this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].column.data) {
            this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].isDataEntered = true;
          } else {
            this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label].isDataEntered = false;
          }
        }
      }
    }
  }

  public focusElements() {
    setTimeout(() => {
      let el: HTMLTextAreaElement | HTMLSelectElement | HTMLInputElement;
      if (document.querySelectorAll('input[type=text]').length > 0) {
        el = document.querySelectorAll('input[type=text]')[0] as HTMLInputElement;
      } else if (document.getElementsByTagName('textarea').length > 0) {
        el = document.getElementsByTagName('textarea')[0];
      } else if (document.getElementsByTagName('select').length > 0) {
        el = document.getElementsByTagName('select')[0];
      }
      el?.focus();
      this.currentFocusElement = el;
    }, 100);
  }

  public tableUpdate(text: any) {
    if (!text.isTableData) {
      return;
    }
    this.template.bodies[text.pageIndex].tables[text.tableIndex].dataSource[text.rowIndex][text.column].column.data = text.text;
    this.template.bodies[text.pageIndex].tables[text.tableIndex].dataSource[text.rowIndex][text.column].data = text.text;
    this.template.bodies[text.pageIndex].tables[text.tableIndex].dataSource[text.rowIndex][text.column].isDataEntered = true;
    this.checkChanges();
  }

  public surveyTableStatusUpdate(pageIndex: number, tableIndex: number, rowIndex: number, columnLabel: string, labelList: Array<any>) {
    labelList.forEach(label => {
      if (label.label === columnLabel) {
        this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][columnLabel].data = true;
        this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][columnLabel].column.data = true;
      } else {
        this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][label.label].data = false;
        this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][columnLabel].column.data = false;
      }
      this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][columnLabel].isDataEntered = true;
    });
    this.checkChanges();
  }

  public surveyTablePriorityUpdate(pageIndex: number, tableIndex: number, rowIndex: number, priority: string, columnLabel: string) {
    this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][columnLabel].data = priority
      .toLowerCase()
      .split('priority')[1]
      ?.trim();
    this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][columnLabel].column.data = priority
      .toLowerCase()
      .split('priority')[1]
      ?.trim();
    this.template.bodies[pageIndex].tables[tableIndex].dataSource[rowIndex][columnLabel].isDataEntered = true;
  }

  public getReportUpdateStatus(): boolean {
    return this.isReportUpdating || false;
  }

  public hasPendingWrites(): boolean {
    return this.#saveTimerID !== undefined || this.isReportUpdating;
  }

  private getWidthAndHeights(style: ElementsStyleObject, element: ReportElement): ElementsStyleObject {
    if (element.length) {
      if (element.isHorizontal) {
        style.width = element.length + this.template.sizeUnit;
        style.height = element.height ? element.height + this.template.sizeUnit : 'auto';
        style.borderTop = `3px ${element.style} ${element.color}`;
      } else {
        style.width = element.width ? element.width + this.template.sizeUnit : 'auto';
        style.height = element.length + this.template.sizeUnit;
        style.borderRight = `3px ${element.style} ${element.color}`;
      }
    } else {
      style.width = element.width ? element.width + this.template.sizeUnit : 'auto';
      style.height = element.height ? element.height + this.template.sizeUnit : 'auto';
      if (element.border) {
        style.border = element.border;
      } else {
        style.borderWidth = element.borderWidth + this.template.sizeUnit;
        style.borderStyle = element.borderStyle;
        style.borderColor = element.borderColor;
      }
    }
    return style;
  }

  private scrollToCurrentFocusElementOnlyForIos(): void {
    if (platform.name === OscPlatform.IOs) {
      this.#plt.ready().then(() => {
        window.addEventListener('keyboardWillShow', e => {
          this.currentFocusElement?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        });
      });
    }
  }

  private scaleReport(): void {
    setTimeout(() => {
      const displayedWidth = document.getElementById('inspectionReportHtml')?.clientWidth;
      const reportElement = document.getElementById('report-element');
      const reportContentPage = document.getElementsByClassName('ipm-report__page-frame').item(0)?.clientWidth;
      this.scale = Math.min(displayedWidth / reportContentPage, 1.0);
      if (!!reportElement && !Number.isNaN(this.scale)) {
        (reportElement as any).style.transform = `scale(${this.scale})`;
        (reportElement as any).style.transformOrigin = `0 0`;
        this.changeDetectorRef.detectChanges();
      }
    }, 500);
  }

  private calculateMeasurementRowHeight(
    cmlLabel: string,
    row: SurveyMeasurement,
    column: ListItem,
    measurementPoints: TemplateMeasurementPoint,
  ): number {
    let rowHeight = 0;
    if (column.type === 'Editable') {
      if (column.inputType === 'select') {
        rowHeight = 14.82; // 14.82 is the height of mat-select component in mm
      } else if (column.inputType === 'checkbox') {
        rowHeight = this.getMeasurementTextLineHeight(measurementPoints);
      } else if (column.inputType === 'text') {
        if (measurementPoints.dataSource) {
          let cmlData = '';
          measurementPoints.dataSource.forEach(cml => {
            cml.forEach(cmlRow => {
              if (cmlRow.id.includes(cmlLabel + '-' + row.id)) {
                cmlData = cmlRow[column.label];
              }
            });
          });
          rowHeight =
            cmlData && cmlData !== ''
              ? this.estimateMeasurementCellTextHeight(cmlData, measurementPoints, column)
              : this.getEditableMeasurementCellMinHeight();
        } else {
          rowHeight = this.getEditableMeasurementCellMinHeight();
        }
      }
    } else {
      let data = '';
      if (column.dataField === 'id') {
        data = cmlLabel + '-' + row.id;
      } else {
        data = row[column.dataField];
      }
      rowHeight = this.estimateMeasurementCellTextHeight(data, measurementPoints, column);
      if (column.type === 'Dynamic-Editable') {
        rowHeight = Math.max(rowHeight, this.getEditableMeasurementCellMinHeight());
      }
    }
    return rowHeight;
  }

  private getEditableMeasurementCellMinHeight(): number {
    return this.pxToMm(20); // 20px to match the minHeight from the template.
  }

  private estimateMeasurementCellTextHeight(data: unknown, measurementPoints: TemplateMeasurementPoint, column: ListItem): number {
    const text = String(data ?? '');
    const availableWidth = this.getMeasurementColumnContentWidth(measurementPoints, column);
    const normalizedText = text.replace(/<br\s*\/?\s*>/gi, '\n');
    const lineCount = normalizedText
      .split(/\r?\n/)
      .reduce(
        (lineTotal, line) =>
          lineTotal + Math.max(1, Math.ceil(this.calculateDescriptionLengthInMM(line, measurementPoints.fontSize) / availableWidth)),
        0,
      );

    return lineCount * this.getMeasurementTextLineHeight(measurementPoints);
  }

  private getMeasurementColumnContentWidth(measurementPoints: TemplateMeasurementPoint, column: ListItem): number {
    const columnWidth = (this.getRenderedMeasurementTableWidth(measurementPoints) * column.widthRatio) / 100;

    return Math.max(1, columnWidth - 2 * measurementPoints.cellMargin);
  }

  private getRenderedMeasurementTableWidth(measurementPoints: TemplateMeasurementPoint): number {
    const pageContentWidth =
      this.getPageWidth() - (this.getDimensionValue(this.template.pageMarginLeft) + this.getDimensionValue(this.template.pageMarginRight));

    return pageContentWidth - 2 * measurementPoints.tableMargin;
  }

  private getMeasurementTextLineHeight(measurementPoints: TemplateMeasurementPoint): number {
    return 1.35 * measurementPoints.fontSize;
  }

  private getMeasurementTableFixedHeight(measurementPoints: TemplateMeasurementPoint): number {
    return (
      this.measurementTableTitleHeightInMm +
      this.getMeasurementTextLineHeight(measurementPoints) +
      2 * measurementPoints.cellMargin +
      2 * (measurementPoints.tableMargin + measurementPoints.tableBorderWidth) +
      this.measurementTableHeightSafetyBufferInMm
    );
  }

  private repaginateMeasurementPointFromRenderedHeights(sourcePageIndex: number): void {
    const pageIndex = this.template.bodies.findIndex(page => page.measurementPoints?.[0]?.dataSourceIndex === sourcePageIndex);
    if (pageIndex === -1) {
      return;
    }

    const measurementPoint = this.template.bodies[pageIndex].measurementPoints[0];
    const renderedTableHeights = this.getRenderedMeasurementTableHeights(sourcePageIndex);
    if (renderedTableHeights.length === 0) {
      return;
    }

    const pageDefinitions = this.createMeasurementPagesFromRenderedHeights(renderedTableHeights);
    if (pageDefinitions.length === 0) {
      return;
    }

    const originalPage = this.template.bodies[pageIndex];
    const firstMeasurementPoint = this.cloneMeasurementPointForRenderedPage(measurementPoint, pageDefinitions[0]);
    firstMeasurementPoint.dataSourceIndex = sourcePageIndex;
    this.template.bodies[pageIndex].measurementPoints = [firstMeasurementPoint];

    let insertIndex = pageIndex + 1;
    while (
      this.template.bodies[insertIndex]?.pageType === 'custom' &&
      this.template.bodies[insertIndex].measurementPoints?.[0]?.dataSourceIndex === sourcePageIndex
    ) {
      this.template.bodies.splice(insertIndex, 1);
    }

    pageDefinitions.slice(1).forEach(pageDefinition => {
      const customMeasurementPoint = this.cloneMeasurementPointForRenderedPage(measurementPoint, pageDefinition);
      customMeasurementPoint.dataSourceIndex = sourcePageIndex;
      this.template.bodies.splice(insertIndex, 0, {
        pageType: 'custom',
        hasHeader: originalPage.hasHeader,
        hasFooter: originalPage.hasFooter,
        measurementPoints: [customMeasurementPoint],
      });
      insertIndex++;
    });
  }

  private getRenderedMeasurementTableHeights(
    sourcePageIndex: number,
  ): Array<{ tableIndex: number; fixedHeight: number; rowHeights: number[] }> {
    const tableElements = Array.from(document.querySelectorAll<HTMLElement>(`[data-measurement-source-page-index="${sourcePageIndex}"]`));

    const tableHeightsByIndex = new Map<number, { tableIndex: number; fixedHeight: number; rowHeights: number[] }>();

    tableElements.forEach(tableElement => {
      const tableIndex = Number(tableElement.getAttribute('data-measurement-table-index'));
      const renderedRows = Array.from(tableElement.querySelectorAll<HTMLElement>('[data-measurement-row-index]')).map(rowElement => ({
        rowIndex: Number(rowElement.getAttribute('data-measurement-row-index')),
        height: this.pxToMm(rowElement.getBoundingClientRect().height),
      }));
      const rowHeights = renderedRows.map(row => row.height);
      const fixedHeight =
        this.pxToMm(tableElement.getBoundingClientRect().height) - rowHeights.reduce((total, height) => total + height, 0);
      const tableHeights = tableHeightsByIndex.get(tableIndex) ?? { tableIndex, fixedHeight, rowHeights: [] };
      tableHeights.fixedHeight = Math.max(tableHeights.fixedHeight, fixedHeight);
      renderedRows.forEach(row => {
        tableHeights.rowHeights[row.rowIndex] = row.height;
      });
      tableHeightsByIndex.set(tableIndex, tableHeights);
    });

    return Array.from(tableHeightsByIndex.values()).map(tableHeights => ({
      ...tableHeights,
      rowHeights: tableHeights.rowHeights.filter(height => height !== undefined),
    }));
  }

  private createMeasurementPagesFromRenderedHeights(
    renderedTableHeights: Array<{ tableIndex: number; fixedHeight: number; rowHeights: number[] }>,
  ): Array<Array<{ tableIndex: number; startRow: number; endRow: number }>> {
    const pages: Array<Array<{ tableIndex: number; startRow: number; endRow: number }>> = [];
    const pageCapacity =
      this.getMeasurementPageAvailableSpace() - this.measurementTableTitleHeightInMm - this.measurementTableDomPaginationSafetyBufferInMm;
    let currentPage: Array<{ tableIndex: number; startRow: number; endRow: number }> = [];
    let availableSpace = pageCapacity;

    renderedTableHeights.forEach(renderedTableHeight => {
      let startRow = 0;
      while (startRow < renderedTableHeight.rowHeights.length) {
        const fixedHeight = renderedTableHeight.fixedHeight;
        let usedHeight = fixedHeight;
        let endRow = startRow;

        while (endRow < renderedTableHeight.rowHeights.length && usedHeight + renderedTableHeight.rowHeights[endRow] <= availableSpace) {
          usedHeight += renderedTableHeight.rowHeights[endRow];
          endRow++;
        }

        if (endRow === startRow && currentPage.length > 0) {
          pages.push(currentPage);
          currentPage = [];
          availableSpace = pageCapacity;
          continue;
        }

        if (endRow === startRow) {
          endRow++;
          usedHeight += renderedTableHeight.rowHeights[startRow];
        }

        currentPage.push({ tableIndex: renderedTableHeight.tableIndex, startRow, endRow });
        availableSpace -= usedHeight;
        startRow = endRow;

        if (startRow < renderedTableHeight.rowHeights.length) {
          pages.push(currentPage);
          currentPage = [];
          availableSpace = pageCapacity;
        }
      }
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    return pages;
  }

  private cloneMeasurementPointForRenderedPage(
    measurementPoint: TemplateMeasurementPoint,
    pageDefinition: Array<{ tableIndex: number; startRow: number; endRow: number }>,
  ): TemplateMeasurementPoint {
    return {
      ...measurementPoint,
      tableIndex: pageDefinition.map(definition => definition.tableIndex),
      startRow: pageDefinition[0].startRow,
      endRow: pageDefinition[pageDefinition.length - 1].endRow,
    };
  }

  private getMeasurementRowId(label: string, reading: SurveyMeasurement): string {
    if (!label) {
      return reading.id;
    }
    return reading.isCustom === true ? `*${label}-${reading.id}` : `${label}-${reading.id}`;
  }

  private pxToMm(px: number): number {
    return px * this.#millimetersPerCssPixel;
  }

  private getMeasurementPageAvailableSpace(): number {
    return (
      this.getPageHeight() -
      (this.getDimensionValue(this.template.pageMarginTop) + this.getDimensionValue(this.template.pageMarginBottom)) -
      (this.getDimensionValue(this.template.header.height) + this.getDimensionValue(this.template.footer.height))
    );
  }

  private isFullMeasurementPageSpaceAvailable(availableSpace: number): boolean {
    return availableSpace >= this.getMeasurementPageAvailableSpace() - this.availableSpaceOffsetInMm;
  }

  private splitVisualEventData(originalVisualEvents: VisualEvent[]): VisualEvent[] {
    const splitVisualEvents: VisualEvent[] = [];

    originalVisualEvents.forEach(originalEvent => {
      originalEvent.images.forEach(image => {
        const splitEvent: VisualEvent = {
          ...originalEvent,
          images: [{ fileId: image.fileId, imageUrl: image.imageUrl }],
        };
        splitVisualEvents.push(splitEvent);
      });
    });
    splitVisualEvents.sort((a, b) =>
      a.visualEventType.localeCompare(b.visualEventType, undefined, { numeric: true, sensitivity: 'variant' }),
    );
    return splitVisualEvents;
  }

  private calculateDescriptionLengthInMM(descriptionText: string, fontSize = 3) {
    const calSpan = document.createElement('span');
    document.body.appendChild(calSpan);
    calSpan.style.font = 'normal';
    calSpan.style.fontSize = fontSize + 'mm';
    calSpan.style.height = 'auto';
    calSpan.style.width = 'auto';
    calSpan.style.position = 'absolute';
    calSpan.style.whiteSpace = 'no-wrap';
    calSpan.innerHTML = descriptionText;

    const widthPx = Math.ceil(calSpan.clientWidth);
    const widthPxToMM = this.pxToMm(widthPx);

    document.body.removeChild(calSpan);

    return widthPxToMM;
  }

  private getReportHtmlData(): string {
    const report: string = document
      .getElementById('inspectionReportHtml')
      .innerHTML.replace(/ng-reflect-model="true"/g, 'checked')
      .replace(/Choose an option/g, '')
      .replace(
        /<!--[\s\S]*?-->|class="ng-star-inserted"|ng-reflect-ng-style="\[object Object]"|ng-reflect-ng-class="\[object Object]"/g,
        '',
      )
      .replace(/class="ipm-report__object-fit"/g, 'style="object-fit: contain !important;"')
      .replace(/ng-star-inserted/g, '')
      .replace(/overflow-wrap:/g, 'word-wrap:');
    return report;
  }

  private checkMeasurementData(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if (this.inspectionType === InspectionTypes.UT || this.inspectionType === InspectionTypes.UT_Baseline) {
        const combinedMeasurementData: SurveyPoint[] = [];
        this.measurementData.forEach(itm => {
          combinedMeasurementData.push(...itm.points);
        });

        let isCommentColumnAvailable = false;
        let isMeasurementDataAvailable = false;

        this.template.bodies.map(page => {
          if (page.measurementPoints && page.measurementPoints.length > 0) {
            isMeasurementDataAvailable = true;
            page.measurementPoints[0].columns.map(res => {
              if (res.label === 'Comments') {
                isCommentColumnAvailable = true;
              }
            });
          }
        });

        combinedMeasurementData.forEach(el => {
          const readings = el.ids;
          readings.map(row => {
            if (isCommentColumnAvailable && isMeasurementDataAvailable) {
              if (
                (row.status === 'Skipped' || this.isMeasurementInAlarmState(row.measurement, row.previousMeasurement)) &&
                (row.comment === '' || row.comment === null || row.comment === '*required')
              ) {
                resolve(false);
              }
            }
            if (isMeasurementDataAvailable) {
              if (row.status !== 'Skipped' && (row.measurement === 0 || row.measurement === -1)) {
                resolve(false);
              }
            }
          });
        });

        resolve(true);
      } else {
        resolve(true);
      }
    });
  }

  private checkSurveyTableData(): boolean {
    let isSurveyTable = true;
    if (this.inspectionType === InspectionTypes.Visual || this.inspectionType === InspectionTypes.Visual_Baseline) {
      for (const element of this.template.bodies) {
        if (Object.hasOwn(element, 'tables') && element.tables.length > 0) {
          for (const table of element.tables.filter(itm => itm.tableType === 'surveyTable')) {
            if (Object.hasOwn(table, 'dataSource') && table.dataSource.length > 0) {
              for (const row of table.dataSource) {
                const result = Object.values(row);
                const radioFieldArray = result.filter((x: any) => x?.inputType === 'radio');
                const textFieldArray = result.filter((x: any) => x?.inputType === 'text' || x?.inputType === 'textarea');
                if (this.checkVisualEventRow(radioFieldArray, textFieldArray)) {
                  row.isShow = false;
                } else {
                  row.isShow = true;
                  isSurveyTable = false;
                }
              }
            }
          }
        }
      }
    }
    return isSurveyTable;
  }

  private checkVisualEventRow(radioFieldArray: any[], textFieldArray: any[]): boolean {
    if (!radioFieldArray.length) {
      return true;
    }
    const selectedRadioFields = radioFieldArray.filter(item => item.column.data);
    if (!selectedRadioFields.length) {
      const hasRequiredFields = radioFieldArray.some(item => item.row?.requiredField);
      return !hasRequiredFields;
    }

    // All fields with needsPriority set in Report Template Builder
    // must have all fields filled before signing a report
    const fieldsWithNeedsPriority = selectedRadioFields.filter(radioField => radioField.column?.needsPriority);
    const isAllFieldsWithNeedsPriorityFilled = fieldsWithNeedsPriority.every(field => {
      const selectedVisualEvent = this.allVisualEventData.find(visualEvent => visualEvent.visualEventType === field.row.label);
      const recommendation = selectedVisualEvent.visualEventRecommendation;
      const description = selectedVisualEvent.description;
      const priority = selectedVisualEvent.visualEventPriority;

      return (
        recommendation &&
        description &&
        priority &&
        (selectedVisualEvent.locations.length > 0 || selectedVisualEvent.wholeEquipment) &&
        selectedVisualEvent.images.length > 0
      );
    });
    if (!isAllFieldsWithNeedsPriorityFilled) {
      return false;
    }

    const hasAnySelectedRadioRequiredField = selectedRadioFields.some(
      radioField => radioField.row.requiredField && radioField.column?.needsPriority,
    );
    if (!hasAnySelectedRadioRequiredField) {
      return true;
    }

    if (!textFieldArray.length) {
      return true;
    }

    return textFieldArray.every(textField => textField.column.data !== undefined && textField.column.data !== '');
  }

  private isReportTextFieldValid(): boolean {
    let isTextFieldValid = true;

    this.template.bodies.forEach(body => {
      body.texts?.forEach(text => {
        this.validateTextInput(text);
        if (!text.isValid) {
          isTextFieldValid = false;
        }
      });
    });

    return isTextFieldValid;
  }

  private validateTextInput(text) {
    text.isValid = true;
    text.isEmpty = false;

    if (text.type === 'dynamic-editable') {
      // The mobile report data is an offline snapshot (JSON round-tripped through
      // SQLite), so an unfilled dynamic field can arrive as more than just
      // '', null or undefined — e.g. a whitespace-only default or an empty
      // multiSelect array. Normalize before deciding emptiness so a required
      // field still blocks signing on mobile, not just on web.
      if (this.isDynamicFieldEmpty(text.text)) {
        text.isEmpty = true;
        if (text?.validation?.required) {
          text.isValid = false;
        }
      }

      if (text.inputType === 'multiSelect' || text.inputType === 'select') {
        this.validateSelectInput(text);
      }
    }
  }

  private validateSelectInput(text) {
    if (text.options && !this.isDynamicFieldEmpty(text.text)) {
      text.isValid = true;
      if (text.inputType === 'multiSelect') {
        text.text.forEach((val, ind) => {
          text.text[ind] = ' ' + val;
        });
      }
    }
  }

  private isDynamicFieldEmpty(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return String(value).trim() === '';
  }

  private sortVisualEvents<T>(a: T, b: T, property: keyof T): number {
    const getLeadingNumber = (str: string): number => {
      const match = str.match(/^\d+/);
      return match ? parseInt(match[0], 10) : Infinity;
    };

    const valueA = String(a[property]);
    const valueB = String(b[property]);

    const numA = getLeadingNumber(valueA);
    const numB = getLeadingNumber(valueB);

    if (numA !== numB) {
      return numA - numB;
    }

    return valueA.localeCompare(valueB, undefined, {
      numeric: true,
      sensitivity: 'variant',
      ignorePunctuation: true,
    });
  }

  //All the photos are inserted as a visual event, even if the photo is not associated to a pick list item.
  private getPhotoEventsNotLinkedToVisualEvents(): VisualEvent[] {
    const allPhotoEvents = this.visualEvents.filter(x => x.isPhotoOnlyEvent);
    const allVisualEvents = this.visualEvents.filter(x => !x.isPhotoOnlyEvent);
    const photosNotLinkedToVisualEvents: VisualEvent[] = [];

    allPhotoEvents.forEach(photoEvent => {
      photoEvent.images.forEach(photoEventImage => {
        if (allVisualEvents.some(ve => ve.images.some(i => i.fileId === photoEventImage.fileId))) {
          return;
        }

        photosNotLinkedToVisualEvents.push(photoEvent);
      });
    });
    return photosNotLinkedToVisualEvents;
  }

  private estimateGenericTableRowHeight(
    rowData: Record<string, unknown>,
    columnList: { width: number; dataField: string }[],
    charWidthMm: number,
    lineHeightMm: number,
  ): number {
    const baseRowHeightMm = 14;
    let maxLines = 1;
    for (const col of columnList) {
      const text = String(rowData[col.dataField] ?? '');
      const charsPerLine = Math.max(1, Math.floor(col.width / charWidthMm));
      maxLines = Math.max(maxLines, Math.ceil(text.length / charsPerLine) || 1);
    }
    return Math.max(baseRowHeightMm, maxLines * lineHeightMm);
  }

  private createGenericTablePages(
    tableData: any[],
    pageCapacity: number,
    columnList: { width: number; dataField: string }[],
    charWidthMm: number,
    lineHeightMm: number,
  ): any[][] {
    const pages: any[][] = [];
    let currentPage: any[] = [];
    let currentHeight = 0;
    for (const row of tableData) {
      const rowHeight = this.estimateGenericTableRowHeight(row, columnList, charWidthMm, lineHeightMm);
      // if the row height added to actual height exceed the max height, add the row in a new page
      if (currentHeight + rowHeight > pageCapacity && currentPage.length > 0) {
        pages.push(currentPage);
        currentPage = [row];
        currentHeight = rowHeight;
      } else {
        // Otherwise, add the row in the current page
        currentPage.push(row);
        currentHeight += rowHeight;
      }
    }
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    return pages;
  }

  private repaginateMeasurementPointsFromRenderedHeights(): void {
    const sourcePageIndexes: number[] = [
      ...new Set<number>(
        this.template.bodies
          .filter(page => page.measurementPoints?.length > 0)
          .map(page => page.measurementPoints[0].dataSourceIndex)
          .filter((dataSourceIndex: number | undefined): dataSourceIndex is number => dataSourceIndex !== undefined),
      ),
    ];

    sourcePageIndexes.forEach(sourcePageIndex => this.repaginateMeasurementPointFromRenderedHeights(sourcePageIndex));
  }
}
